import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, tap, map } from 'rxjs';
import { AdoUserProfile } from '../models/ado-user-profile';
import { AdoAccount } from '../models/ado-account';
import { ReleaseDefinition } from '../models/release-definition';
import { AdoResponse } from '../models/ado-response';
import { AdoProject } from '../models/ado-project';
import { CreateBuildPipelineRequest } from '../models/create-build-pipeline-request';
import { CreateRepositoryRequest } from '../models/create-repository-request';
import { CreateBranchRequest } from '../models/create-branch-request';
import { ReleaseDefinitionRevision } from '../models/release-definition-revision';
import { ServiceConnection } from '../mario/models/service-connections';
import { AdoPullRequestSearchCriteria } from '../models/ado-pull-request-search-criteria';

@Injectable({
  providedIn: 'root',
})
export class AdoService {
  private readonly getReleaseDefinitionByIdUri: string =
    'https://vsrm.dev.azure.com/{org}/{project}/_apis/release/definitions/{definitionId}?api-version=6.1-preview.4';
  private readonly getReleaseDefinitionHistoryUri: string =
    'https://vsrm.dev.azure.com/{org}/{project}/_apis/Release/definitions/{definitionId}/revisions?api-version=7.1-preview.1';
  private readonly getReleaseDefinitionRevisionUri: string =
    'https://vsrm.dev.azure.com/{org}/{project}/_apis/Release/definitions/{definitionId}/revisions/{revision}?api-version=7.1-preview.1';
  private readonly getServiceConnectionsUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/serviceendpoint/endpoints?api-version=7.1-preview.4';    
  private readonly getBuildDefinitionsUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/build/definitions?api-version=6.1-preview.7';
  private readonly definitionsByIdUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/build/definitions/{definitionId}?api-version=7.1';
  private readonly createPipelineUri: string =
    'https://vsrm.dev.azure.com/{org}/{project}/_apis/release/definitions?api-version=6.1-preview.4';
  private readonly createBuildPipelineUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/pipelines?api-version=7.1-preview.1';
  private readonly getReleaseDefinitionsUri: string =
    'https://vsrm.dev.azure.com/{org}/{project}/_apis/release/definitions?api-version=6.1-preview.4&searchText=-rc&top=1000';
  private readonly getAllReleaseDefinitionsUri: string =
    'https://vsrm.dev.azure.com/{org}/{project}/_apis/release/definitions?api-version=6.1-preview.4';
  private readonly getAccountsUri: string =
    'https://app.vssps.visualstudio.com/_apis/accounts?api-version=6.1-preview.1&memberId={memberId}';
  private readonly getUserProfileUri: string =
    'https://app.vssps.visualstudio.com/_apis/profile/profiles/me?api-version=6.0';
  private readonly getProjectsUri: string =
    'https://dev.azure.com/{org}/_apis/projects?api-version=6.0';
  private readonly getReposUri: string = 
    'https://dev.azure.com/{org}/{project}/_apis/git/repositories?api-version=7.1-preview.1'
  private readonly getRepoByIdUri: string = 
    'https://dev.azure.com/{org}/{project}/_apis/git/repositories/{repositoryId}?api-version=7.1-preview.1'
  private readonly getRepositoryFilesUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/git/repositories/{repositoryId}/items?recursionLevel=Full&api-version=7.1-preview.1'
  private readonly getRepositoryFileUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/git/repositories/{repositoryId}/items?path={filePath}&versionDescriptor.version=master&$format=text&api-version=7.1-preview.1'
  private readonly createRepoUri: string =
    'https://dev.azure.com/{org}/_apis/git/repositories?api-version=7.1-preview.1';
  private readonly branchesUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/git/repositories/{repositoryId}/refs?api-version=7.1-preview.1'
  private readonly updateRepoUri: string =
    'https://dev.azure.com/{org}/_apis/git/repositories/{repositoryId}?api-version=7.1-preview.1';
  private readonly createPullRequestUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/git/repositories/{repositoryId}/pullrequests?api-version=7.0';
  private readonly pushChangesUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/git/repositories/{repositoryId}/pushes?api-version=7.1';
  private readonly getPullRequestsUri: string = 
    'https://dev.azure.com/{org}/{project}/_apis/git/repositories/{repositoryId}/pullrequests';
  private readonly getPullRequestThreadsUri: string =
    'https://dev.azure.com/{org}/{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/threads?api-version=7.1-preview.1';
  private readonly getReleaseByIdUri: string = 
    'https://vsrm.dev.azure.com/{org}/{project}/_apis/release/releases/{releaseId}?api-version=7.1';
  private readonly getBoardColumnsUri: string = 
    'https://dev.azure.com/{org}/{project}/{team}/_apis/work/boards/Backlog%20Items/columns?api-version=7.1-preview.1';
  private readonly wiqlUri: string = 
    'https://dev.azure.com/{org}/{project}/_apis/wit/wiql?timePrecision=true&api-version=7.1';
  private readonly getWorkItemRevisionsUri: string = 
    'https://dev.azure.com/{org}/{project}/_apis/wit/workitems/{id}/revisions?api-version=7.1';

  private adoProfile: AdoUserProfile | null = null;
  private adoProjectsByOrg: Record<string, AdoResponse<AdoProject>> = {};
  private adoRepositoryById: Record<string, any> = {};

  constructor(private readonly http: HttpClient) {}

  GetUserProfile(): Observable<AdoUserProfile> {
    if(this.adoProfile) {
      return of(this.adoProfile);
    }
    return this.http.get<AdoUserProfile>(this.getUserProfileUri, {
      observe: 'body',
    }).pipe(
      tap(profile => this.adoProfile = profile)
    );
  }

  GetAccounts(memberId: string): Observable<AdoResponse<AdoAccount>> {
    return this.http.get<AdoResponse<AdoAccount>>(
      this.getAccountsUri.replace('{memberId}',memberId),
      { observe: 'body' }
    );
  }

  GetProjects(org: string): Observable<AdoResponse<AdoProject>> {
    if(this.adoProjectsByOrg[org]) {
      return of(this.adoProjectsByOrg[org]);
    }
    
    return this.http.get<AdoResponse<AdoProject>>(
      this.getProjectsUri.replace('{org}',org),
      { observe: 'body' }
    ).pipe(
      tap(projects => {
        this.adoProjectsByOrg[org] = projects;
      })
    );
  }

  GetReleaseDefinitions(
    org: string,
    project: string
  ): Observable<ReleaseDefinition> {
    let uri = this.getReleaseDefinitionsUri
      .replace('{org}', org)
      .replace('{project}', project);
    return this.http.get<ReleaseDefinition>(uri, {
      observe: 'body',
    });
  }

  GetReleaseDefinitionHistory(
    org: string,
    project: string,
    definitionId: string
  ): Observable<ReleaseDefinitionRevision[]> {
    let uri = this.getReleaseDefinitionHistoryUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{definitionId}', definitionId);
    return this.http.get<ReleaseDefinitionRevision[]>(uri, {
      observe: 'body',
    });
  }

  GetReleaseDefinitionRevision(
    org: string,
    project: string,
    definitionId: string,
    revision: string
  ): Observable<ReleaseDefinition> {
    let uri = this.getReleaseDefinitionRevisionUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{definitionId}', definitionId)
      .replace('{revision}', revision);
    return this.http.get<ReleaseDefinition>(uri, {
      observe: 'body',
    });
  }

  GetServiceConnections(
    org: string,
    project: string,
  ): Observable<ServiceConnection[]> {
    let uri = this.getServiceConnectionsUri
      .replace('{org}', org)
      .replace('{project}', project)
    return this.http.get<ServiceConnection[]>(uri, {
      observe: 'body',
    });
  }

  GetAllReleaseDefinitions(
    org: string,
    project: string
  ): Observable<ReleaseDefinition> {
    let uri = this.getAllReleaseDefinitionsUri
      .replace('{org}', org)
      .replace('{project}', project);
    return this.http.get<ReleaseDefinition>(uri, {
      observe: 'body',
    });
  }

  GetBuildDefinitions(
    org: string,
    project: string
  ): Observable<any> {
    let uri = this.getBuildDefinitionsUri
      .replace('{org}', org)
      .replace('{project}', project)
    return this.http.get<any>(uri, {
      observe: 'body',
    });
  }

  GetBuildDefinitionById(
    org: string,
    project: string,
    definitionId: number
  ): Observable<any> {
    let uri = this.definitionsByIdUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{definitionId}', definitionId.toString());
    return this.http.get<any>(uri, {
      observe: 'body',
    });
  }

  GetReleaseDefinitionById(
    org: string,
    project: string,
    definitionId: string
  ): Observable<any> {
    let uri = this.getReleaseDefinitionByIdUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{definitionId}', definitionId);
    return this.http.get<any>(uri, {
      observe: 'body',
    });
  }
  
  CreatePipeline(
    org: string,
    project: string,
    body: any
  ): Observable<any> {
    let uri = this.createPipelineUri
      .replace('{org}', org)
      .replace('{project}', project);
    return this.http.post<any>(uri, body, {
      observe: 'response',
    });
  }

  CreateBuildPipeline(
    org: string,
    project: string,
    body: CreateBuildPipelineRequest
  ): Observable<any> {
    let uri = this.createBuildPipelineUri
      .replace('{org}', org)
      .replace('{project}', project);
    return this.http.post<any>(uri, body, {
      observe: 'body',
    });
  }

  GetRepositories(
    org: string,
    project: string,
  ): Observable<any> {
    let uri = this.getReposUri
      .replace('{org}', org)
      .replace('{project}', project)
    return this.http.get<any>(uri, {
      observe: 'body',
    });
  }

  GetRepositoryById(
    org: string,
    project: string,
    id: string
  ): Observable<any> {
    if(this.adoRepositoryById[id]) {
      return of(this.adoRepositoryById[id]);
    }
    let uri = this.getRepoByIdUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{repositoryId}', id);
    return this.http.get<any>(uri, {
      observe: 'body',
    }).pipe(
      tap(repository => {
        this.adoRepositoryById[id] = repository;
      })
    );
  }

  CreateRepository(
    org: string,
    body: CreateRepositoryRequest
  ): Observable<any> {
    let uri = this.createRepoUri
      .replace('{org}', org)
    return this.http.post<any>(uri, body, {
      observe: 'body',
    });
  }

  GetRepositoryFiles(
    org: string,
    project: string,
    repositoryId: string
  ): Observable<any> {
    let uri = this.getRepositoryFilesUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{repositoryId}', repositoryId);
    return this.http.get<any>(uri, {
      observe: 'body',
    });
  }


  GetRepositoryFileAsText(
    org: string,
    project: string,
    repositoryId: string,
    filePath: string
  ): Observable<string> {
    let uri = this.getRepositoryFileUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{repositoryId}', repositoryId)
      .replace('{filePath}', filePath);
    return this.http.get(uri, {
      responseType: 'text'
    });
  }

  UpdateRepository(
    org: string,
    repositoryId: string,
    body: any
  ): Observable<any> {
    let uri = this.updateRepoUri
      .replace('{org}', org)
      .replace('{repositoryId}', repositoryId)
    return this.http.patch<any>(uri, body, {
      observe: 'body',
    });
  }

  GetBranches(
    org: string,
    project: string,
    repositoryId: string
  ): Observable<any> {
    let uri = this.branchesUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{repositoryId}', repositoryId);
    return this.http.get<any>(uri, {
      observe: 'body',
    });
  }

  CreateBranch(
    org: string,
    project: string,
    repositoryId: string,
    body: CreateBranchRequest[]
  ): Observable<any> {
    let uri = this.branchesUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{repositoryId}', repositoryId);
    return this.http.post<any>(uri, body, {
      observe: 'body',
    });
  }

  CreatePullRequest(org: string, project: string, repositoryId: string, request: any): Observable<any> {
    let uri = this.createPullRequestUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{repositoryId}', repositoryId);
    return this.http.post<any>(uri, request, {
      observe: 'body',
    });
  }

  PushChanges(org: string, project: string, repositoryId: string, request: any): Observable<any> {
    let uri = this.pushChangesUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{repositoryId}', repositoryId);
    return this.http.post<any>(uri, request, {
      observe: 'body',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  GetPullRequests(org: string, project: string, repositoryId: string, searchCriteria?: AdoPullRequestSearchCriteria): Observable<any> {
    let baseUri = this.getPullRequestsUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{repositoryId}', repositoryId);

    let params = new HttpParams();
    params = params.append('api-version', '7.1');

    if (searchCriteria) {
      for (const key in searchCriteria) {
        if (searchCriteria.hasOwnProperty(key) && (searchCriteria as any)[key] !== undefined && (searchCriteria as any)[key] !== null) {
          params = params.append(key, (searchCriteria as any)[key].toString());
        }
      }
    }

    return this.http.get<any>(baseUri, {
      observe: 'body',
      params: params
    });
  }

  GetPullRequestThreads(org: string, project: string, repositoryId: string, pullRequestId: number): Observable<any> {
    const uri = this.getPullRequestThreadsUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{repositoryId}', repositoryId)
      .replace('{pullRequestId}', pullRequestId.toString());
    return this.http.get<any>(uri, {
      observe: 'body',
    });
  }

  GetReleaseById(
    org: string,
    project: string,
    releaseId: string
  ): Observable<any> {
    let uri = this.getReleaseByIdUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{releaseId}', releaseId);
    return this.http.get<any>(uri, {
      observe: 'body',
    });
  }

  ExecuteWiqlQuery(org: string, project: string, query: string): Observable<any> {
    const url = this.wiqlUri
      .replace('{org}', org)
      .replace('{project}', project);
    return this.http.post(url, { query });
  }

  GetWorkItemRevisions(org: string, project: string, id: number): Observable<any> {
    const url = this.getWorkItemRevisionsUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{id}', id.toString());
    return this.http.get<any>(url).pipe(map(response => response.value));
  }

  GetBoardColumns(org: string, project: string, team: string): Observable<any[]> {
    const url = this.getBoardColumnsUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{team}', team);
    return this.http.get<any>(url).pipe(map(response => response.value));
  }

  UpdateBuildDefinition(org: string, project: string, definitionId: number, definition: any): Observable<any> {
    const url = this.definitionsByIdUri
      .replace('{org}', org)
      .replace('{project}', project)
      .replace('{definitionId}', definitionId.toString());
    return this.http.put(url, definition);
  }
}
